/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * CombinedTrendChart - Dual-line trend chart showing Math and R&W together
 *
 * Enhanced to show:
 * - Solid lines for actual scores (from challenge satScores)
 * - Dashed lines for projected scores (trend extrapolation)
 * - Uncertainty cone for projections
 * - Goal line visualization
 *
 * Uses Recharts for rendering.
 */

import { useMemo, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts'
import { Share2, TrendingUp, TrendingDown, Minus, Calendar, Target, Info } from 'lucide-react'
import useChartTheme from '../../hooks/useChartTheme'

// ── Colors ──────────────────────────────────────────────────────────────
const MATH_COLOR = '#3b82f6'
const RW_COLOR = '#10b981'
const GOAL_COLOR = '#f59e0b'
const REPORT_COLORS = {
  ACTUAL_SAT: '#f59e0b', // gold
  PSAT_NMSQT: '#a855f7', // purple
  PSAT_10: '#a855f7',
  PSAT_8_9: '#a855f7',
  PRACTICE_TEST: '#06b6d4' // cyan
}

// Custom dot for score report markers on the chart
const ScoreReportDot = ({ cx, cy, payload, dataKey }) => {
  if (cx == null || cy == null || payload?.[dataKey] == null) return null
  const type = payload.reportTestType
  const color = REPORT_COLORS[type] || REPORT_COLORS.ACTUAL_SAT
  const size = type === 'ACTUAL_SAT' ? 8 : 7

  // Star shape for actual SAT
  if (type === 'ACTUAL_SAT') {
    const points = []
    for (let i = 0; i < 5; i++) {
      const outerAngle = (i * 72 - 90) * (Math.PI / 180)
      const innerAngle = (i * 72 + 36 - 90) * (Math.PI / 180)
      points.push(`${cx + size * Math.cos(outerAngle)},${cy + size * Math.sin(outerAngle)}`)
      points.push(
        `${cx + size * 0.4 * Math.cos(innerAngle)},${cy + size * 0.4 * Math.sin(innerAngle)}`
      )
    }
    return <polygon points={points.join(' ')} fill={color} stroke="#fff" strokeWidth={1.5} />
  }
  // Diamond for PSAT
  if (type?.startsWith('PSAT')) {
    return (
      <polygon
        points={`${cx},${cy - size} ${cx + size},${cy} ${cx},${cy + size} ${cx - size},${cy}`}
        fill={color}
        stroke="#fff"
        strokeWidth={1.5}
      />
    )
  }
  // Triangle for practice
  return (
    <polygon
      points={`${cx},${cy - size} ${cx + size * 0.87},${cy + size * 0.5} ${cx - size * 0.87},${cy + size * 0.5}`}
      fill={color}
      stroke="#fff"
      strokeWidth={1.5}
    />
  )
}

const formatTooltipDate = (ts) => {
  if (!ts) return ''
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Custom tooltip for the combined trend chart.
 * Defined outside the component to avoid recreation on every render.
 */
const REPORT_TYPE_LABELS = {
  ACTUAL_SAT: 'SAT',
  PSAT_NMSQT: 'PSAT/NMSQT',
  PSAT_10: 'PSAT 10',
  PSAT_8_9: 'PSAT 8/9',
  PRACTICE_TEST: 'Practice'
}

const CombinedTooltip = ({ active, payload, theme, coneContext }) => {
  if (!active || !payload?.length) return null
  const validPayloads = payload.filter((p) => p.value != null && !p.dataKey?.startsWith('cone'))
  // Deduplicate by dataKey -- Area fills and Lines share the same dataKey
  const seen = new Set()
  const uniquePayloads = validPayloads.filter((p) => {
    if (seen.has(p.dataKey)) return false
    seen.add(p.dataKey)
    return true
  })
  const date = uniquePayloads[0]?.payload?.date || payload[0]?.payload?.date
  const isInCone = coneContext && date && date > Date.now()

  // Nothing to show if no data points and not in cone
  if (uniquePayloads.length === 0 && !isInCone) return null

  // Interpolate cone values when hovering in projection zone
  let coneLines = []
  if (isInCone && coneContext) {
    const { mathConeData, rwConeData } = coneContext
    const interpolateCone = (coneData, label, color) => {
      if (!coneData || coneData.length < 2) return null
      const start = coneData[0]
      const end = coneData[1]
      const t = Math.min(1, Math.max(0, (date - start.date) / (end.date - start.date)))
      const low = Math.round(start.coneLow + t * (end.coneLow - start.coneLow))
      const high = Math.round(
        start.coneLow +
          start.coneBand +
          t * (end.coneLow + end.coneBand - start.coneLow - start.coneBand)
      )
      return { label, color, low, high }
    }
    const mathCone = interpolateCone(mathConeData, 'Math', MATH_COLOR)
    const rwCone = interpolateCone(rwConeData, 'R&W', RW_COLOR)
    if (mathCone) coneLines.push(mathCone)
    if (rwCone) coneLines.push(rwCone)
  }

  // Check for score report markers
  const reportPayloads = uniquePayloads.filter((p) => p.dataKey?.startsWith('report'))
  const regularPayloads = uniquePayloads.filter((p) => !p.dataKey?.startsWith('report'))
  const reportType = payload[0]?.payload?.reportTestType

  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-lg border"
      style={{
        backgroundColor: theme?.tooltip?.bg,
        borderColor: theme?.tooltip?.border,
        color: theme?.tooltip?.text
      }}
    >
      <p className="opacity-75 mb-1">{formatTooltipDate(date)}</p>
      {regularPayloads.map((p) => (
        <p key={p.dataKey} className="font-semibold" style={{ color: p.color }}>
          {p.name}: {p.value}
        </p>
      ))}
      {reportPayloads.map((p) => (
        <p key={p.dataKey} className="font-semibold" style={{ color: p.color }}>
          {p.name} ({REPORT_TYPE_LABELS[reportType] || 'Test'}): {p.value}
        </p>
      ))}
      {coneLines.map((c) => (
        <p key={c.label} className="font-semibold" style={{ color: c.color }}>
          {c.label} (projected): {c.low}-{c.high}
        </p>
      ))}
    </div>
  )
}

/**
 * CombinedTrendChart - Overlayed Math and R&W trend chart with actual vs projected
 */
const CombinedTrendChart = ({
  mathActualHistory = [],
  mathPredictedHistory = [],
  rwActualHistory = [],
  rwPredictedHistory = [],
  mathUncertainty = null,
  rwUncertainty = null,
  scoreReports = [],
  goalScore = null,
  goalDate = null,
  height = 280,
  showConfidenceBands = true,
  showLegend = true,
  showDataPoints = true,
  showGrid = true,
  showProjections = true,
  // When true, the chart fits the container (no forced horizontal scroll, no
  // auto-scroll-to-latest). Use for compact dashboard summaries that should
  // glance without interaction.
  fitContainer = false,
  onShare,
  className = '',
  // Legacy props for backwards compatibility
  mathHistory = [],
  rwHistory = []
}) => {
  const theme = useChartTheme()

  // Use legacy props if new props are empty (backwards compatibility)
  const effectiveMathActual = mathActualHistory.length > 0 ? mathActualHistory : mathHistory
  const effectiveRwActual = rwActualHistory.length > 0 ? rwActualHistory : rwHistory

  // Prepare chart data - separate actual from projected
  const chartSeries = useMemo(() => {
    const processHistory = (history, isProjected = false) =>
      history
        .map((entry) => ({
          date: new Date(entry.createdAt || entry.completedAt || entry.date).getTime(),
          score: entry.satScore || entry.predictedScore || entry.score,
          isProjected
        }))
        .filter((d) => d.score != null && !isNaN(d.date))
        .sort((a, b) => a.date - b.date)

    return {
      mathActual: processHistory(effectiveMathActual, false),
      mathProjected: showProjections ? processHistory(mathPredictedHistory, true) : [],
      rwActual: processHistory(effectiveRwActual, false),
      rwProjected: showProjections ? processHistory(rwPredictedHistory, true) : []
    }
  }, [
    effectiveMathActual,
    mathPredictedHistory,
    effectiveRwActual,
    rwPredictedHistory,
    showProjections
  ])

  // Merge all data into a unified time-series for Recharts
  const {
    rechartsData,
    chartMetrics,
    hasCone,
    futureTs,
    xTicks,
    xDomain,
    mathConeData,
    rwConeData,
    minChartWidth
  } = useMemo(() => {
    // Collect all unique timestamps for score series
    const dateMap = new Map()

    const addPoints = (series, key) => {
      for (const p of series) {
        if (!dateMap.has(p.date)) dateMap.set(p.date, {})
        dateMap.get(p.date)[key] = p.score
      }
    }

    addPoints(chartSeries.mathActual, 'mathActual')
    addPoints(chartSeries.mathProjected, 'mathProjected')
    addPoints(chartSeries.rwActual, 'rwActual')
    addPoints(chartSeries.rwProjected, 'rwProjected')

    // Add score report data points (split by section)
    for (const report of scoreReports) {
      const ts = new Date(report.testDate).getTime()
      if (isNaN(ts)) continue
      if (!dateMap.has(ts)) dateMap.set(ts, {})
      const entry = dateMap.get(ts)
      if (report.mathScore) entry.reportMath = report.mathScore
      if (report.rwScore) entry.reportRw = report.rwScore
      entry.reportTestType = report.testType
    }

    // Build separate cone datasets (2 points each) — kept outside the unified
    // dataset so stacked areas don't get polluted by intermediate null rows.
    const coneEndTs = Date.now() + 30 * 24 * 60 * 60 * 1000
    let mathConeData = null
    let rwConeData = null

    if (showConfidenceBands && mathUncertainty && chartSeries.mathActual.length > 0) {
      const last = chartSeries.mathActual[chartSeries.mathActual.length - 1]
      mathConeData = [
        { date: last.date, coneLow: last.score, coneBand: 0 },
        {
          date: coneEndTs,
          coneLow: mathUncertainty.low,
          coneBand: mathUncertainty.high - mathUncertainty.low
        }
      ]
    }

    if (showConfidenceBands && rwUncertainty && chartSeries.rwActual.length > 0) {
      const last = chartSeries.rwActual[chartSeries.rwActual.length - 1]
      rwConeData = [
        { date: last.date, coneLow: last.score, coneBand: 0 },
        {
          date: coneEndTs,
          coneLow: rwUncertainty.low,
          coneBand: rwUncertainty.high - rwUncertainty.low
        }
      ]
    }

    const hasCone = !!(mathConeData || rwConeData)
    const futureTs = hasCone ? coneEndTs : null

    // Add an empty entry at the cone endpoint so the X-axis domain extends to it
    if (futureTs && !dateMap.has(futureTs)) {
      dateMap.set(futureTs, {})
    }

    // Build sorted array
    const data = Array.from(dateMap.entries())
      .map(([date, values]) => ({ date, ...values }))
      .sort((a, b) => a.date - b.date)

    // Calculate score domain
    const allScores = [
      ...chartSeries.mathActual.map((d) => d.score),
      ...chartSeries.mathProjected.map((d) => d.score),
      ...chartSeries.rwActual.map((d) => d.score),
      ...chartSeries.rwProjected.map((d) => d.score)
    ]
    for (const r of scoreReports) {
      if (r.mathScore) allScores.push(r.mathScore)
      if (r.rwScore) allScores.push(r.rwScore)
    }
    if (goalScore) allScores.push(goalScore)
    if (mathUncertainty) allScores.push(mathUncertainty.low, mathUncertainty.high)
    if (rwUncertainty) allScores.push(rwUncertainty.low, rwUncertainty.high)

    let minScore = 200
    let maxScore = 800
    if (allScores.length > 0) {
      const minData = Math.min(...allScores)
      const maxData = Math.max(...allScores)
      const padding = (maxData - minData) * 0.15 || 50
      minScore = Math.max(200, Math.floor((minData - padding) / 10) * 10)
      maxScore = Math.min(800, Math.ceil((maxData + padding) / 10) * 10)
    }

    // Generate explicit X-axis ticks, domain padding, and minimum chart width
    const DAY = 86400000
    const MIN_PX_PER_DAY = 15
    let xTicks = null
    let minChartWidth = 0
    let xDomain = ['dataMin', 'dataMax']

    if (data.length > 0) {
      const minTs = data[0].date
      const maxTs = data[data.length - 1].date
      const range = maxTs - minTs
      const totalDays = range / DAY

      // Pad X domain so edge dots aren't clipped by the Y-axis
      const xPadding = Math.max(range * 0.02, 2 * DAY)
      xDomain = [minTs - xPadding, maxTs + xPadding]

      // Minimum chart width so data points aren't cramped — forces horizontal scroll
      minChartWidth = Math.max(Math.round(totalDays * MIN_PX_PER_DAY), 400)

      // Use weekly ticks — the scrollable container gives us room
      let interval
      if (range <= 14 * DAY) interval = 2 * DAY
      else if (range <= 60 * DAY) interval = 7 * DAY
      else interval = 14 * DAY

      const ticks = []
      const startDay = Math.floor(minTs / DAY) * DAY
      for (let t = startDay; t <= maxTs + interval; t += interval) {
        if (t >= minTs - interval) ticks.push(t)
      }
      // Ensure a tick near the data start so it's labeled
      if (ticks.length > 0 && ticks[0] - minTs > interval * 0.5) {
        ticks.unshift(Math.floor(minTs / DAY) * DAY)
      }
      if (futureTs && !ticks.some((t) => Math.abs(t - futureTs) < interval * 0.3)) {
        ticks.push(futureTs)
      }
      // Deduplicate and sort
      xTicks = [...new Set(ticks)].sort((a, b) => a - b)
    }

    return {
      rechartsData: data,
      chartMetrics: { minScore, maxScore },
      hasCone,
      futureTs,
      xTicks,
      xDomain,
      mathConeData,
      rwConeData,
      minChartWidth
    }
  }, [chartSeries, scoreReports, goalScore, mathUncertainty, rwUncertainty, showConfidenceBands])

  // Calculate trends from actual data
  const trends = useMemo(() => {
    const calcTrend = (data) => {
      if (data.length < 2) return { direction: 'stable', change: 0 }
      const first = data[0].score
      const last = data[data.length - 1].score
      const change = last - first
      if (change > 10) return { direction: 'improving', change }
      if (change < -10) return { direction: 'declining', change }
      return { direction: 'stable', change }
    }

    return {
      math: calcTrend(chartSeries.mathActual),
      rw: calcTrend(chartSeries.rwActual)
    }
  }, [chartSeries])

  // Auto-scroll so "Today" is visible on mount. Skipped when the chart is
  // fitting its container (no scroll to apply).
  const scrollRef = useRef(null)
  useEffect(() => {
    if (fitContainer) return
    const el = scrollRef.current
    if (!el || !minChartWidth) return
    // If we know the x-axis numeric domain, position Today at ~70% across the
    // visible viewport (recent history visible to the left, projection cone to
    // the right). Otherwise fall back to a reasonable default.
    let scrollTarget
    if (
      Array.isArray(xDomain) &&
      typeof xDomain[0] === 'number' &&
      typeof xDomain[1] === 'number'
    ) {
      const [minTs, maxTs] = xDomain
      const t = Math.max(0, Math.min(1, (Date.now() - minTs) / (maxTs - minTs)))
      scrollTarget = Math.max(0, t * el.scrollWidth - el.clientWidth * 0.7)
    } else {
      scrollTarget = Math.max(0, el.scrollWidth * 0.35 - el.clientWidth * 0.5)
    }
    el.scrollLeft = scrollTarget
    // Depend on the numeric domain bounds rather than the array reference so
    // this only re-runs when the domain actually changes (xDomain is already
    // memoized, but primitives make the intent explicit).
  }, [fitContainer, minChartWidth, xDomain?.[0], xDomain?.[1]])

  const hasAnyData =
    chartSeries.mathActual.length > 0 ||
    chartSeries.mathProjected.length > 0 ||
    chartSeries.rwActual.length > 0 ||
    chartSeries.rwProjected.length > 0 ||
    scoreReports.length > 0

  // Render empty state
  if (!hasAnyData) {
    return (
      <div
        className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 flex flex-col items-center justify-center ${className}`}
        style={{ minHeight: height }}
      >
        <Calendar className="text-gray-400 dark:text-gray-500 mb-3" size={32} />
        <p className="text-gray-600 dark:text-gray-300 font-medium">No score history yet</p>
        <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Complete challenges to build your score history
        </span>
      </div>
    )
  }

  const formatDate = (ts) => {
    if (!ts) return ''
    return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }

  // Trend indicator component
  const TrendBadge = ({ trend, color, label }) => (
    <div className="flex items-center gap-1.5">
      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <div className="flex items-center gap-0.5" style={{ color }}>
        {trend.direction === 'improving' && <TrendingUp size={12} />}
        {trend.direction === 'declining' && <TrendingDown size={12} />}
        {trend.direction === 'stable' && <Minus size={12} />}
        <span className="text-xs font-medium">
          {trend.change > 0 ? '+' : ''}
          {trend.change}
        </span>
      </div>
    </div>
  )

  // Single point indicator
  const hasSinglePoint =
    (chartSeries.mathActual.length === 1 && chartSeries.rwActual.length === 0) ||
    (chartSeries.rwActual.length === 1 && chartSeries.mathActual.length === 0)

  const dataPointCount = chartSeries.mathActual.length + chartSeries.rwActual.length

  // Date range for footer
  const allDates = rechartsData.map((d) => d.date)
  const minDate = allDates.length > 0 ? new Date(Math.min(...allDates)) : new Date()
  const maxDate = allDates.length > 0 ? new Date(Math.max(...allDates)) : new Date()

  return (
    <div
      className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Score Trend</h4>
          {showLegend && (
            <div className="flex items-center gap-4">
              {chartSeries.mathActual.length > 0 && (
                <TrendBadge trend={trends.math} color={MATH_COLOR} label="Math" />
              )}
              {chartSeries.rwActual.length > 0 && (
                <TrendBadge trend={trends.rw} color={RW_COLOR} label="R&W" />
              )}
            </div>
          )}
        </div>
        {onShare && (
          <button
            onClick={onShare}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
            title="Share Progress"
          >
            <Share2 size={18} />
          </button>
        )}
      </div>

      {/* Single point notice */}
      {hasSinglePoint && (
        <div className="flex items-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 text-xs">
          <Info size={14} />
          <span>Early estimate - complete more challenges for accurate trends</span>
        </div>
      )}

      {/* Chart — horizontally scrollable when data spans a wide time range */}
      <div
        ref={scrollRef}
        className="px-2 py-4"
        style={{
          overflowX: fitContainer ? 'hidden' : 'auto',
          overflowY: 'hidden'
        }}
      >
        <div style={{ minWidth: fitContainer ? 0 : minChartWidth, height: height - 100 }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
            <ComposedChart data={rechartsData} margin={{ top: 5, right: 50, bottom: 5, left: 10 }}>
              <defs>
                <linearGradient id="mathAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={MATH_COLOR} stopOpacity={0.15} />
                  <stop offset="100%" stopColor={MATH_COLOR} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="rwAreaGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={RW_COLOR} stopOpacity={0.15} />
                  <stop offset="100%" stopColor={RW_COLOR} stopOpacity={0.02} />
                </linearGradient>
              </defs>

              {showGrid && <CartesianGrid strokeDasharray="4 4" stroke={theme.grid} />}

              <XAxis
                dataKey="date"
                type="number"
                domain={xDomain}
                ticks={xTicks || undefined}
                tickFormatter={formatDate}
                tick={{ fill: theme.axisLabel, fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: theme.grid }}
              />
              <YAxis
                yAxisId="left"
                domain={[chartMetrics.minScore, chartMetrics.maxScore]}
                allowDataOverflow={true}
                tick={{ fill: theme.axisLabel, fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={40}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[chartMetrics.minScore, chartMetrics.maxScore]}
                allowDataOverflow={true}
                tick={{ fill: theme.axisLabel, fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                width={40}
              />

              <Tooltip
                content={
                  <CombinedTooltip
                    theme={theme}
                    coneContext={hasCone ? { mathConeData, rwConeData } : null}
                  />
                }
              />

              {/* Uncertainty cones — each uses its own 2-point data array */}
              {mathConeData && (
                <>
                  <Area
                    data={mathConeData}
                    type="linear"
                    dataKey="coneLow"
                    stackId="mathCone"
                    yAxisId="left"
                    stroke="none"
                    fill="transparent"
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                    legendType="none"
                  />
                  <Area
                    data={mathConeData}
                    type="linear"
                    dataKey="coneBand"
                    stackId="mathCone"
                    yAxisId="left"
                    stroke="none"
                    fill={MATH_COLOR}
                    fillOpacity={0.12}
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                    legendType="none"
                  />
                </>
              )}
              {rwConeData && (
                <>
                  <Area
                    data={rwConeData}
                    type="linear"
                    dataKey="coneLow"
                    stackId="rwCone"
                    yAxisId="left"
                    stroke="none"
                    fill="transparent"
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                    legendType="none"
                  />
                  <Area
                    data={rwConeData}
                    type="linear"
                    dataKey="coneBand"
                    stackId="rwCone"
                    yAxisId="left"
                    stroke="none"
                    fill={RW_COLOR}
                    fillOpacity={0.12}
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                    legendType="none"
                  />
                </>
              )}

              {/* Goal line */}
              {goalScore && (
                <ReferenceLine
                  yAxisId="left"
                  y={goalScore}
                  stroke={GOAL_COLOR}
                  strokeWidth={2}
                  strokeDasharray="8 4"
                  label={{
                    value: `Goal: ${goalScore}`,
                    position: 'insideTopLeft',
                    fill: GOAL_COLOR,
                    fontSize: 11,
                    fontWeight: 500
                  }}
                />
              )}

              {/* "Today" marker when cone projects into the future */}
              {hasCone && (
                <ReferenceLine
                  yAxisId="left"
                  x={Date.now()}
                  stroke={theme.axisLabel}
                  strokeWidth={1}
                  strokeDasharray="4 4"
                  strokeOpacity={0.5}
                  label={{
                    value: 'Today',
                    position: 'insideTopRight',
                    fill: theme.axisLabel,
                    fontSize: 10,
                    fontWeight: 400
                  }}
                />
              )}

              {/* Area fills under actual lines */}
              {chartSeries.mathActual.length >= 2 && (
                <Area
                  type="monotone"
                  dataKey="mathActual"
                  yAxisId="left"
                  fill="url(#mathAreaGradient)"
                  stroke="none"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls={true}
                />
              )}
              {chartSeries.rwActual.length >= 2 && (
                <Area
                  type="monotone"
                  dataKey="rwActual"
                  yAxisId="left"
                  fill="url(#rwAreaGradient)"
                  stroke="none"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls={true}
                />
              )}

              {/* Math actual line (solid) */}
              {chartSeries.mathActual.length >= 1 && (
                <Line
                  type="monotone"
                  dataKey="mathActual"
                  yAxisId="left"
                  name="Math"
                  stroke={MATH_COLOR}
                  strokeWidth={2.5}
                  dot={
                    showDataPoints
                      ? { r: 4, fill: '#fff', stroke: MATH_COLOR, strokeWidth: 2 }
                      : false
                  }
                  activeDot={
                    showDataPoints
                      ? { r: 6, fill: '#fff', stroke: MATH_COLOR, strokeWidth: 2 }
                      : false
                  }
                  connectNulls={true}
                  animationDuration={400}
                />
              )}

              {/* R&W actual line (solid) */}
              {chartSeries.rwActual.length >= 1 && (
                <Line
                  type="monotone"
                  dataKey="rwActual"
                  yAxisId="left"
                  name="R&W"
                  stroke={RW_COLOR}
                  strokeWidth={2.5}
                  dot={
                    showDataPoints
                      ? { r: 4, fill: '#fff', stroke: RW_COLOR, strokeWidth: 2 }
                      : false
                  }
                  activeDot={
                    showDataPoints
                      ? { r: 6, fill: '#fff', stroke: RW_COLOR, strokeWidth: 2 }
                      : false
                  }
                  connectNulls={true}
                  animationDuration={400}
                />
              )}

              {/* Math projected line (dashed) */}
              {showProjections && chartSeries.mathProjected.length >= 2 && (
                <Line
                  type="monotone"
                  dataKey="mathProjected"
                  yAxisId="left"
                  name="Math (projected)"
                  stroke={MATH_COLOR}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  strokeOpacity={0.7}
                  dot={false}
                  connectNulls={true}
                  animationDuration={400}
                />
              )}

              {/* R&W projected line (dashed) */}
              {showProjections && chartSeries.rwProjected.length >= 2 && (
                <Line
                  type="monotone"
                  dataKey="rwProjected"
                  yAxisId="left"
                  name="R&W (projected)"
                  stroke={RW_COLOR}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  strokeOpacity={0.7}
                  dot={false}
                  connectNulls={true}
                  animationDuration={400}
                />
              )}

              {/* Score report markers -- Math */}
              {scoreReports.length > 0 && (
                <Line
                  type="monotone"
                  dataKey="reportMath"
                  yAxisId="left"
                  name="Math"
                  stroke={MATH_COLOR}
                  strokeWidth={0}
                  dot={<ScoreReportDot dataKey="reportMath" />}
                  activeDot={{ r: 8, fill: '#fff', stroke: MATH_COLOR, strokeWidth: 2 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )}

              {/* Score report markers -- R&W */}
              {scoreReports.length > 0 && (
                <Line
                  type="monotone"
                  dataKey="reportRw"
                  yAxisId="left"
                  name="R&W"
                  stroke={RW_COLOR}
                  strokeWidth={0}
                  dot={<ScoreReportDot dataKey="reportRw" />}
                  activeDot={{ r: 8, fill: '#fff', stroke: RW_COLOR, strokeWidth: 2 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Legend for line types */}
      {showProjections &&
        (chartSeries.mathActual.length > 0 || chartSeries.rwActual.length > 0) && (
          <div className="flex items-center justify-center gap-6 px-4 py-2 border-t border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <div className="w-6 h-0.5 bg-gray-400"></div>
              <span>Actual</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <div
                className="w-6 h-0.5 bg-gray-400 border-dashed"
                style={{ borderTop: '2px dashed' }}
              ></div>
              <span>Projected</span>
            </div>
            {scoreReports.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <div
                  className="w-0 h-0"
                  style={{
                    borderLeft: '5px solid transparent',
                    borderRight: '5px solid transparent',
                    borderBottom: '8px solid #f59e0b'
                  }}
                ></div>
                <span>Score Reports</span>
              </div>
            )}
            {showConfidenceBands && (
              <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <div className="w-4 h-3 bg-gray-300 opacity-30 rounded"></div>
                <span>Uncertainty</span>
              </div>
            )}
          </div>
        )}

      {/* Footer */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400">
        <span>{dataPointCount} data points</span>
        <span>
          {minDate.toLocaleDateString()} - {maxDate.toLocaleDateString()}
        </span>
      </div>
    </div>
  )
}

CombinedTrendChart.propTypes = {
  // New props for actual vs predicted
  mathActualHistory: PropTypes.arrayOf(
    PropTypes.shape({
      completedAt: PropTypes.string,
      satScore: PropTypes.number,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  mathPredictedHistory: PropTypes.arrayOf(
    PropTypes.shape({
      createdAt: PropTypes.string,
      predictedScore: PropTypes.number,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  rwActualHistory: PropTypes.arrayOf(
    PropTypes.shape({
      completedAt: PropTypes.string,
      satScore: PropTypes.number,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  rwPredictedHistory: PropTypes.arrayOf(
    PropTypes.shape({
      createdAt: PropTypes.string,
      predictedScore: PropTypes.number,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  mathUncertainty: PropTypes.shape({
    low: PropTypes.number,
    high: PropTypes.number
  }),
  rwUncertainty: PropTypes.shape({
    low: PropTypes.number,
    high: PropTypes.number
  }),
  scoreReports: PropTypes.arrayOf(
    PropTypes.shape({
      testDate: PropTypes.string,
      mathScore: PropTypes.number,
      rwScore: PropTypes.number,
      testType: PropTypes.string,
      totalScore: PropTypes.number
    })
  ),
  goalScore: PropTypes.number,
  goalDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  // Legacy props for backwards compatibility
  mathHistory: PropTypes.arrayOf(
    PropTypes.shape({
      createdAt: PropTypes.string,
      date: PropTypes.string,
      predictedScore: PropTypes.number,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  rwHistory: PropTypes.arrayOf(
    PropTypes.shape({
      createdAt: PropTypes.string,
      date: PropTypes.string,
      predictedScore: PropTypes.number,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  height: PropTypes.number,
  showConfidenceBands: PropTypes.bool,
  showLegend: PropTypes.bool,
  showDataPoints: PropTypes.bool,
  showGrid: PropTypes.bool,
  showProjections: PropTypes.bool,
  fitContainer: PropTypes.bool,
  onShare: PropTypes.func,
  className: PropTypes.string
}

export default CombinedTrendChart
